import React, { useEffect } from 'react';
import { Calendar, Mail, ShieldCheck, FileCheck2, BarChart3, CheckCircle2, XCircle } from 'lucide-react';
import { Footer } from '../layout/Footer';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { CONTACT_INFO } from '../../constants/data';
import { CONSULTING, CALENDLY_URL } from '../../constants/consulting';

const PILLAR_ICONS = [
  <ShieldCheck className="w-8 h-8 text-blue-600" />,
  <FileCheck2 className="w-8 h-8 text-blue-600" />,
  <BarChart3 className="w-8 h-8 text-blue-600" />
];

// Página en español para quien llega por el QR del brochure: sin búsqueda de empleo ni CV.
export const ConsultingPage: React.FC = () => {
  const c = CONSULTING;

  useEffect(() => {
    const previousTitle = document.title;
    const previousLang = document.documentElement.lang;
    const description = document.querySelector('meta[name="description"]');
    const previousDescription = description?.getAttribute('content') ?? '';

    document.title = c.meta.title;
    document.documentElement.lang = 'es';
    description?.setAttribute('content', c.meta.description);

    return () => {
      document.title = previousTitle;
      document.documentElement.lang = previousLang;
      description?.setAttribute('content', previousDescription);
    };
  }, [c.meta.title, c.meta.description]);

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-800">
      <nav className="bg-white sticky top-0 z-50 shadow-sm border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <a href="/" className="flex-shrink-0 flex items-center gap-2">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center text-white font-bold">
                JA
              </div>
              <span className="font-bold text-xl tracking-tight text-slate-900">
                Aarón García
              </span>
            </a>
            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noreferrer"
              className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-full font-medium transition-all shadow-lg shadow-blue-600/20"
            >
              {c.hero.cta}
            </a>
          </div>
        </div>
      </nav>

      <main>
        <header className="relative bg-white overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-slate-50 to-blue-50/50 z-0"></div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-24 relative z-10">
            <div className="text-center max-w-4xl mx-auto">
              <p className="text-sm font-semibold uppercase tracking-wider text-blue-700 mb-6">
                {c.hero.kicker}
              </p>
              <h1 className="text-4xl md:text-6xl font-extrabold text-slate-900 tracking-tight mb-6 leading-tight">
                {c.hero.title}
              </h1>
              <p className="mt-4 max-w-2xl mx-auto text-xl text-slate-600 leading-relaxed">
                {c.hero.subtitle}
              </p>
              <div className="mt-10 flex justify-center">
                <Button
                  href={CALENDLY_URL}
                  target="_blank"
                  rel="noreferrer"
                  variant="primary"
                  icon={<Calendar size={20} />}
                >
                  {c.hero.cta}
                </Button>
              </div>
            </div>
          </div>
        </header>

        <section className="py-20 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-3 gap-8">
              {c.pillars.map((pillar, index) => (
                <Card key={pillar.title} hover={false}>
                  <div className="mb-5 bg-blue-50 w-14 h-14 rounded-2xl flex items-center justify-center">
                    {PILLAR_ICONS[index]}
                  </div>
                  <h2 className="text-xl font-bold text-slate-900 mb-3">{pillar.title}</h2>
                  <p className="text-slate-600 leading-relaxed">{pillar.desc}</p>
                </Card>
              ))}
            </div>

            <div className="mt-16 max-w-3xl mx-auto text-center">
              <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-500 mb-3">
                {c.audience.title}
              </h2>
              <p className="text-xl text-slate-700 leading-relaxed">{c.audience.desc}</p>
              <p className="mt-6 text-2xl font-bold text-slate-900">{c.audience.question}</p>
            </div>
          </div>
        </section>

        <section className="py-20 bg-white">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">{c.problem.title}</h2>
            <p className="text-lg text-slate-700 leading-relaxed mb-8">{c.problem.intro}</p>
            <ul className="space-y-4">
              {c.problem.items.map((item) => (
                <li key={item} className="flex gap-3 text-slate-700 leading-relaxed">
                  <span className="mt-2 h-2 w-2 flex-shrink-0 rounded-full bg-blue-600"></span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <p className="mt-8 text-lg font-semibold text-slate-900">{c.problem.closing}</p>
          </div>
        </section>

        <section className="py-20 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-12 text-center">
              {c.services.title}
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              {c.services.items.map((service, index) => (
                <Card key={service.name} hover={false} className="flex flex-col">
                  <p className="text-sm font-semibold text-blue-700 mb-2">{index + 1}</p>
                  <h3 className="text-2xl font-bold text-slate-900 mb-1">{service.name}</h3>
                  <p className="text-sm text-slate-500 mb-5">{service.when}</p>
                  <p className="text-slate-700 leading-relaxed mb-4">{service.intro}</p>
                  {service.list.length > 0 && (
                    service.ordered ? (
                      <ol className="list-decimal pl-5 space-y-2 text-slate-700 mb-4">
                        {service.list.map((item) => <li key={item}>{item}</li>)}
                      </ol>
                    ) : (
                      <ul className="space-y-2 text-slate-700 mb-4">
                        {service.list.map((item) => (
                          <li key={item} className="flex gap-2">
                            <CheckCircle2 size={18} className="mt-1 flex-shrink-0 text-blue-600" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    )
                  )}
                  {service.closing && (
                    <p className="text-slate-600 italic leading-relaxed mt-auto">{service.closing}</p>
                  )}
                </Card>
              ))}
            </div>

            <div className="mt-10 max-w-3xl mx-auto bg-blue-600 text-white rounded-2xl p-8">
              <h3 className="text-xl font-bold mb-2">{c.services.guarantee.title}</h3>
              <p className="leading-relaxed text-blue-50">{c.services.guarantee.desc}</p>
            </div>
          </div>
        </section>

        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl mx-auto text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">{c.proof.title}</h2>
              <p className="text-lg text-slate-600 leading-relaxed">{c.proof.intro}</p>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {c.proof.cases.map((item) => (
                <Card key={item.label} hover={false} className="flex flex-col">
                  <p className="text-sm font-semibold text-slate-500 mb-2">{item.label}</p>
                  <h3 className="text-xl font-bold text-slate-900 mb-4">{item.title}</h3>
                  <p className="text-slate-700 leading-relaxed mb-4">{item.body}</p>
                  <p className="text-slate-900 font-semibold italic mt-auto">{item.lesson}</p>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 bg-slate-900 text-white">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-blue-300 mb-4">{c.rule.title}</h2>
            <p className="text-3xl md:text-5xl font-bold mb-6">{c.rule.headline}</p>
            <p className="text-xl text-slate-200 leading-relaxed mb-10">{c.rule.desc}</p>
            <p className="text-slate-400 leading-relaxed">{c.rule.about}</p>
          </div>
        </section>

        <section className="py-20 bg-slate-50">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-8">{c.notThis.title}</h2>
            <ul className="space-y-5">
              {c.notThis.items.map((item) => (
                <li key={item.title} className="flex gap-3">
                  <XCircle size={22} className="mt-0.5 flex-shrink-0 text-slate-400" />
                  <p className="text-slate-700 leading-relaxed">
                    <span className="font-semibold text-slate-900">{item.title}</span> {item.desc}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="py-24 bg-slate-900 text-white">
          <div className="max-w-3xl mx-auto px-4 text-center">
            <h2 className="text-3xl md:text-5xl font-bold mb-6">{c.contact.title}</h2>
            <p className="text-xl text-slate-300 mb-10 font-light">{c.contact.desc}</p>
            <div className="flex flex-col items-center gap-6">
              <a
                href={CALENDLY_URL}
                target="_blank"
                rel="noreferrer"
                className="bg-white text-slate-900 px-8 py-5 rounded-2xl font-bold text-lg hover:bg-blue-50 transition-all flex items-center justify-center gap-3"
              >
                <Calendar size={22} className="text-blue-600" />
                {c.contact.cta}
              </a>
              <a
                href={`mailto:${CONTACT_INFO.email}`}
                className="text-slate-300 hover:text-white transition-colors flex items-center gap-2"
              >
                <Mail size={18} />
                {c.contact.emailLabel}: {CONTACT_INFO.email}
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer showResume={false} lng="es" />
    </div>
  );
};
